import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthState, BookingDetails } from './types';
import { authService } from './services/authService';
import { initializeHotelContext, getCurrentHotelCode, storeHotelCode } from './utils/hotelContext';
import { guestApiClient } from './services/apiClient';
import { useApiKey } from './hooks/useApiKey';
import LoadingScreen from './components/common/LoadingScreen';
import BookingCodeEntry from './components/auth/BookingCodeEntry';
import SecureTokenHandler from './components/auth/SecureTokenHandler';
import SessionExpiryWarning from './components/common/SessionExpiryWarning';
import TableOrderingEntry from './components/tableOrdering/TableOrderingEntry';
import GDShell, { isKioskMode } from './components/gd/GDShell';
import HomeScreen from './components/gd/HomeScreen';
import CheckInWizard from './components/gd/CheckInWizard';
import PayScreen from './components/gd/PayScreen';
import CheckoutScreen from './components/gd/CheckoutScreen';
import { readNextTarget, routeAfterAuth } from './utils/nextTarget';
import HousekeepingScreen from './components/gd/HousekeepingScreen';
import FoodScreen from './components/gd/FoodScreen';
import InfoScreen from './components/gd/InfoScreen';

// gd screens are the standard layout (plain CSS, phone frame on desktop,
// kiosk mode); auth + table-ordering keep their legacy Chakra UI.

// Create QueryClient instance (exported so tests can reset the cache
// between renders — it lives at module scope and would leak otherwise)
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    booking: null,
    accessMethod: null,
    isCheckedIn: false,
    checkInStatus: 'unknown',
    canAccessServices: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const { apiKeySource, isInitialized, hasApiKey } = useApiKey();
  const { t } = useTranslation();

  useEffect(() => {
    // Initialize hotel context from URL or localStorage
    const hotelCode = initializeHotelContext();
    if (hotelCode) {
      guestApiClient.setHotelCode(hotelCode);
    }

    // Capture ?kiosk=1/?kiosk=0 on ANY landing page (lobby tablets open the
    // login page with it, which never mounts GDShell) so the flag persists
    // into the rest of the session.
    isKioskMode();

    // Clear any existing service workers in development
    if (process.env.NODE_ENV === 'development' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(function(registrations) {
        for(let registration of registrations) {
          registration.unregister();
          console.log('Development: Service worker unregistered');
        }
      });
    }

    // Register service worker for PWA (only in production)
    if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js')
          .then((registration) => {
            console.log('SW registered: ', registration);
          })
          .catch((registrationError) => {
            console.log('SW registration failed: ', registrationError);
          });
      });
    }
  }, []);

  // Check for existing authentication after API key is initialized
  useEffect(() => {
    if (isInitialized) {
      if (hasApiKey) {
        console.log(`API key available from: ${apiKeySource}`);
        checkExistingAuth();
      } else {
        console.warn('No API key available. Please add ?k=your_api_key to URL or set REACT_APP_API_KEY');
        setIsLoading(false);
      }
    }
  }, [isInitialized, hasApiKey, apiKeySource]);

  const checkExistingAuth = async () => {
    try {
      // First, try comprehensive session recovery
      const recoverySuccessful = await authService.recoverSession();
      if (recoverySuccessful) {
        console.log('Session recovery successful');
        const storedAuthState = authService.getStoredAuthState();
        if (storedAuthState) {
          setAuthState({
            isAuthenticated: true,
            booking: storedAuthState.booking,
            accessMethod: storedAuthState.accessMethod,
            isCheckedIn: storedAuthState.isCheckedIn,
            checkInStatus: storedAuthState.checkInStatus,
            canAccessServices: storedAuthState.canAccessServices !== false,
          });
        }
      } else {
        // Try to load stored authentication state
        const storedAuthState = authService.getStoredAuthState();
        if (storedAuthState && authService.isAuthenticated()) {
          console.log('Loading stored authentication state');
          setAuthState({
            isAuthenticated: storedAuthState.isAuthenticated,
            booking: storedAuthState.booking,
            accessMethod: storedAuthState.accessMethod,
            isCheckedIn: storedAuthState.isCheckedIn,
            checkInStatus: storedAuthState.checkInStatus,
            canAccessServices: storedAuthState.canAccessServices !== false,
          });
          
          // Skip background validation - if we have stored auth state, trust it
          // Session validation will happen when needed by individual API calls
        } else if (authService.isAuthenticated()) {
          // If we have a token but no stored state, try to validate
          console.log('Token exists but no stored state, validating with backend');
          try {
            const response = await authService.validateSession();
            if (response.success && response.data?.valid) {
              setAuthState({
                isAuthenticated: true,
                booking: response.data.booking,
                accessMethod: 'booking_code',
                isCheckedIn: (response.data as any).is_checked_in || false,
                checkInStatus: (response.data as any).check_in_status || 'unknown',
                canAccessServices: (response.data as any).can_access_services !== false,
              });
            } else {
              console.log('Session validation failed, clearing auth');
              authService.clearAuth();
            }
          } catch (error) {
            console.error('Session validation failed:', error);
            authService.clearAuth();
          }
        } else {
          console.log('No authentication found');
        }
      }
    } catch (error) {
      console.error('Error checking existing auth:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuthSuccess = (
    booking: BookingDetails, 
    method: 'booking_code' | 'mobile_verification' | 'secure_token',
    additionalData?: {
      isCheckedIn?: boolean;
      checkInStatus?: string;
      canAccessServices?: boolean;
    }
  ) => {
    // The authenticated booking's hotel is authoritative: replace any
    // hotel code left over from a previous stay or room-QR scan so
    // anonymous surfaces (/hotel-info, branding) match the current stay.
    if (booking.hotel?.code) {
      storeHotelCode(booking.hotel.code);
    }
    setAuthState({
      isAuthenticated: true,
      booking,
      accessMethod: method,
      isCheckedIn: additionalData?.isCheckedIn || false,
      checkInStatus: additionalData?.checkInStatus || 'unknown',
      canAccessServices: additionalData?.canAccessServices !== false, // Default to true unless explicitly false
    });
  };

  // Called after a successful self check-in so routing, service access
  // and the persisted session all flip to "in-house" without re-login.
  const handleCheckInComplete = () => {
    authService.updateCheckInStatus('checked_in');
    setAuthState(prev => ({
      ...prev,
      isCheckedIn: true,
      checkInStatus: 'checked_in',
      canAccessServices: true,
    }));
  };

  const handleLogout = async () => {
    await authService.logout();
    setAuthState({
      isAuthenticated: false,
      booking: null,
      accessMethod: null,
      isCheckedIn: false,
      checkInStatus: 'unknown',
      canAccessServices: false,
    });
  };

  // Home adapts to check-in state itself (check-in tile vs services
  // first) — unless the URL carries a ?next= target (reception QR
  // codes, state-aware email links), which wins for a guest who
  // already has a session and would otherwise bounce to home.
  const getDefaultRoute = () => routeAfterAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  // The API key is only ever consumed by tableOrderingService.ts (the
  // /ordering/table QR-menu flow) — verified by grep, nothing else attaches
  // it. Every other route (auth, hotel-info, pre-checkin, dashboard) is a
  // guest-token or fully public flow that never needs it. Gating the whole
  // app behind this key meant the room-QR "hotel info, no restriction
  // whatsoever" page could be walled off by an unrelated missing config —
  // scope the error screen to the one flow that actually needs the key.
  // A table QR printed from the POS carries the table's ?code= instead,
  // which the ordering endpoints accept on their own.
  const hasTableCode = !!(new URLSearchParams(window.location.search).get('code')
    || sessionStorage.getItem('t_code'));
  if (isInitialized && !hasApiKey && !hasTableCode && window.location.pathname.startsWith('/ordering/table')) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh', 
        padding: '20px',
        textAlign: 'center'
      }}>
        <h2>{t('app.configRequired.title')}</h2>
        <p style={{ maxWidth: '30em' }}>{t('app.configRequired.body')}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: '16px',
            padding: '10px 24px',
            fontSize: '1em',
            cursor: 'pointer',
          }}
        >
          {t('app.configRequired.retry')}
        </button>
        <p style={{ fontSize: '0.8em', color: '#666', marginTop: '24px' }}>
          API Key Source: {apiKeySource}
        </p>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <SessionExpiryWarning />
      <Router>
        <Routes>
            {/* Public Routes */}
            {/* With a hotel context the hotel's own page IS the app's home
                for anonymous guests — the login form lives at /login and is
                offered from there. Without a context (no room QR yet) the
                booking-code form stays the landing page. */}
            <Route
              path="/"
              element={
                authState.isAuthenticated ?
                  <Navigate to={getDefaultRoute()} replace /> :
                  getCurrentHotelCode() ?
                    <Navigate to={{ pathname: '/hotel-info', search: window.location.search }} replace /> :
                    <GDShell><BookingCodeEntry onAuthSuccess={handleAuthSuccess} /></GDShell>
              }
            />
            {/* A reception QR (?next=checkin|checkout) is authoritative:
                it ALWAYS asks for a booking code, wiping any session
                first — the desk's printed code can be scanned by any
                guest, and inheriting the previous guest's stay would be
                far worse than one extra code entry. */}
            <Route
              path="/login"
              element={
                authState.isAuthenticated && !readNextTarget() ?
                  <Navigate to={getDefaultRoute()} replace /> :
                  <GDShell><BookingCodeEntry onAuthSuccess={handleAuthSuccess} /></GDShell>
              }
            />
            {/* Secure/booking links ALWAYS re-authenticate with the URL's
                token — never bounce to an existing session's dashboard.
                A stale session for a different guest showing another
                booking's data is worse than a redundant login. The entry
                component wipes the old session before authenticating. */}
            <Route
              path="/booking/:token"
              element={<SecureTokenHandler onAuthSuccess={handleAuthSuccess} />}
            />
            <Route
              path="/secure/:token"
              element={
                <GDShell>
                  <BookingCodeEntry onAuthSuccess={handleAuthSuccess} isSecureToken={true} />
                </GDShell>
              }
            />

            {/* Table Ordering Route - No authentication required */}
            <Route path="/ordering/table" element={<TableOrderingEntry />} />

            {/* Hotel Information (digital guest book) - No authentication required,
                works straight from the room QR code before/without login */}
            <Route
              path="/hotel-info"
              element={
                <GDShell>
                  <InfoScreen
                    isAuthenticated={authState.isAuthenticated}
                    fallbackHotelCode={authState.booking?.hotel?.code}
                  />
                </GDShell>
              }
            />
            <Route path="/info" element={<Navigate to="/hotel-info" replace />} />

            {/* Protected Routes — standard gd layout */}
            {authState.isAuthenticated ? (
              <>
                <Route
                  path="/home"
                  element={
                    <GDShell>
                      <HomeScreen booking={authState.booking!} onLogout={handleLogout} />
                    </GDShell>
                  }
                />
                <Route
                  path="/checkin"
                  element={
                    <GDShell>
                      <CheckInWizard
                        booking={authState.booking!}
                        onCheckInComplete={handleCheckInComplete}
                      />
                    </GDShell>
                  }
                />
                <Route
                  path="/pay"
                  element={
                    <GDShell>
                      <PayScreen booking={authState.booking!} />
                    </GDShell>
                  }
                />
                <Route
                  path="/checkout"
                  element={
                    <GDShell>
                      <CheckoutScreen booking={authState.booking!} />
                    </GDShell>
                  }
                />
                <Route
                  path="/housekeeping"
                  element={
                    <GDShell>
                      <HousekeepingScreen booking={authState.booking!} />
                    </GDShell>
                  }
                />
                <Route
                  path="/food"
                  element={
                    <GDShell>
                      <FoodScreen booking={authState.booking!} />
                    </GDShell>
                  }
                />
                {/* Legacy paths from bookmarks, emails and old QR codes */}
                <Route path="/dashboard" element={<Navigate to="/home" replace />} />
                <Route path="/pre-checkin" element={<Navigate to="/checkin" replace />} />
                <Route path="/ordering" element={<Navigate to="/food" replace />} />
                <Route path="/services" element={<Navigate to="/housekeeping" replace />} />
              </>
            ) : (
              <Route path="*" element={<Navigate to="/" replace />} />
            )}

            {/* Catch all route */}
            <Route
              path="*"
              element={
                <Navigate
                  to={authState.isAuthenticated ? "/home" : "/"}
                  replace
                />
              }
            />
          </Routes>
        </Router>
    </QueryClientProvider>
  );
}

export default App;
